import { React, Fragment, useState, useCallback, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import Modal from "react-responsive-modal";

import "bootstrap/dist/css/bootstrap.min.css";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorIcon from "@mui/icons-material/Error";
import EditIcon from "@mui/icons-material/Edit";
import "./AddressAutoComplete.scss";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import HomeIcon from "@mui/icons-material/Home";
import { Alert, Divider, CircularProgress } from "@mui/material";
import { apiAddressToCoords, apiRevGeocodeLocationGoogle, apiGetAutocompleteList } from "utils/API/TrxNowAPI";
import { countrySearchWhitelist } from "utils/constants/CountySearchWhitelist";
import HelpIcon from "@mui/icons-material/Help";
import { FormControlLabel, Radio, RadioGroup, FormControl } from "@mui/material";
import { devLog } from "utils/devLog";

import { CheckBox } from "@mui/icons-material";

function validateAddressResult(result) {
  let address_has_needed_componenets = false;

  let bHasRoute = false;
  let bHasCity = false;
  let bHasState = false;
  let bHasCountry = false;
  let bHasZip = false;

  for (let j = 0, jLen = result.address_components.length; j < jLen; j++) {
    const comp = result.address_components[j];
    if (!comp || !Array.isArray(comp?.types)) {
      continue;
    }

    if (comp.types.indexOf("route") >= 0) {
      bHasRoute = true;
    } else if (comp.types.indexOf("intersection") >= 0) {
      bHasRoute = true;
    } else if (comp.types.indexOf("plus_code") >= 0) {
      bHasRoute = true;
    } else if (comp.types.indexOf("locality") >= 0) {
      bHasCity = true;
    } else if (comp.types.indexOf("neighborhood") >= 0) {
      bHasCity = true;
    } else if (comp.types.indexOf("administrative_area_level_3") >= 0) {
      bHasCity = true;
    } else if (comp.types.indexOf("administrative_area_level_1") >= 0) {
      bHasState = true;
    } else if (comp.types.indexOf("country") >= 0) {
      bHasCountry = true;
    } else if (comp.types.indexOf("postal_code") >= 0) {
      bHasZip = true;
    }
  }
  return bHasRoute && bHasCity && bHasState && bHasCountry && bHasZip;
}
function AddressAutoComplete(props) {
  const { t } = useTranslation();
  const [addressInput, setAddressInput] = useState(props.addressInput || "");
  const [autocompleteList, setAutocompleteList] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(props.currentLocation);
  const [addressComplete, setAddressComplete] = useState(true);
  const [TTLDisablementAddress, setTTLDisablementAddress] = useState(true);
  const [addressList, setAddressList] = useState();
  const [tmpCurrentAddress, setTmpCurrentAddress] = useState("");
  const [initialCount, setInitialCount] = useState(0);
  const [displayAutoCompleteList, setDisplayAutoCompleteList] = useState(props.displayAutoCompleteList || false);

  // Store original location and address - only set once when first geolocation is received
  const [originalLocation, setOriginalLocation] = useState();
  const [originalAddress, setOriginalAddress] = useState("");
  const hasSavedOriginal = useRef(false);
  const userHasSelectedLocation = useRef(false);
  const hasShownInitialSuggestions = useRef(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialCount === 0) {
      const count = 1;
      devLog("initialCount: ", initialCount);

      setInitialCount(count);
      if (typeof props.getLocationList === "function") {
        const locationList = props.getLocationList(props.addressInput, props.currentLocation);
        setAutocompleteList(locationList);
        updateAddressSelected(false);
        setDisplayAutoCompleteList(true);
        props.onDisplayAutoCompleteList?.(true);
        setInitialCount(count + 1);
        return;
      }
    }
  }, [initialCount]);

  useEffect(() => {
    setDisplayAutoCompleteList(props.displayAutoCompleteList);
  }, [props.displayAutoCompleteList]);

  useEffect(() => {
    devLog("locationList autocomplete: ", props.locationList);
    if (props.locationList && props.locationList.length === 1) {
      devLog("locationList: ", props.locationList[0]);
      setAddressInput(props.locationList[0].formatted_address);
    } else if (props.locationList && props.locationList.length > 0) {
      setAutocompleteList(props.locationList);
      updateAddressSelected(false);
      setDisplayAutoCompleteList(true);
      props.onDisplayAutoCompleteList?.(true);
    }
  }, [props.locationList]);

  const callAPIAutoCompleteList = (address, lat, lng) => {
    apiGetAutocompleteList(address, lat, lng, (locationsList) => {
      if (typeof locationsList !== "object" || !Array.isArray(locationsList.results)) {
        setAutocompleteList([]);
        setDisplayAutoCompleteList(false);
        updateAddressSelected(false);
        return;
      }
      const newLocationsList = locationsList.results.filter((result) => {
        if (!validateAddressResult(result)) {
          return false;
        }
        let bIsValidCountry = false;
        for (let i = 0; i < result.address_components.length; i++) {
          const comp = result.address_components[i];
          if (
            Array.isArray(comp.types) &&
            comp.types.findIndex((type) => type === "country") >= 0 &&
            countrySearchWhitelist.indexOf(comp.short_name) >= 0
          ) {
            bIsValidCountry = true;
            break;
          }
        }
        return bIsValidCountry;
      });
      setAutocompleteList(newLocationsList);
      updateAddressSelected(false);
      setDisplayAutoCompleteList(true);
      props.onDisplayAutoCompleteList?.(true);
    });
  };

  useEffect(() => {
    // Only save if we haven't saved before AND we have valid location and address
    if (!hasSavedOriginal.current && props.currentLocation && props.addressInput) {
      setOriginalLocation(props.currentLocation);
      setOriginalAddress(props.addressInput);
      hasSavedOriginal.current = true;
      // devLog("Saved original location:", props.currentLocation);
      // devLog("Saved original address:", props.addressInput);

      // Show initial autocomplete suggestions on first load when location is available
      // BUT only if user hasn't already manually selected a location
      if (!hasShownInitialSuggestions.current && !userHasSelectedLocation.current) {
        hasShownInitialSuggestions.current = true;
        const lat = props.currentLocation.lat;
        const lng = props.currentLocation.lng;
        callAPIAutoCompleteList(props.addressInput, lat, lng);
      }
    }
  }, [props.currentLocation, props.addressInput]);

  // Sync currentLocation from props, but only if user hasn't manually selected a location
  useEffect(() => {
    if (!userHasSelectedLocation.current && props.currentLocation) {
      setCurrentLocation(props.currentLocation);
    }
  }, [props.currentLocation]);
  const [onHighway, setOnHighway] = useState(props.onHighway || "");

  useEffect(() => {
    setAddressInput(props.addressInput || "");
    setTmpCurrentAddress(props.addressInput || "");
  }, [props.addressInput]);

  useEffect(() => {
    if (props.addressComplete === true) {
      setAddressComplete(true);
    } else {
      setAddressComplete(false);
      console.error("addressComplete: " + addressComplete);
    }
  }, [props.addressComplete]);

  // handleAddressInput - request autocomplete address based on input
  const handleAddressInput = async (address) => {
    setAddressComplete(true);
    if (address && address.length > 3) {
      devLog("currentLocation", currentLocation);
      let lat;
      let lng;
      if (currentLocation) {
        lat = currentLocation.lat;
        lng = currentLocation.lng;
      }
      // request here.com autocomplete address+
      apiGetAutocompleteList(address, lat, lng, (locationsList) => {
        if (typeof locationsList !== "object" || !Array.isArray(locationsList.results)) {
          setAutocompleteList([]);
          setDisplayAutoCompleteList(false);
          updateAddressSelected(false);
          return;
        }
        devLog("LocationsList", locationsList.results);
        const newLocationsList = locationsList.results.filter((result) => {
          if (!validateAddressResult(result)) {
            devLog("validateAddressResult: " + validateAddressResult(result));
            return false;
          }
          let bIsValidCountry = false;

          for (let i = 0; i < result.address_components.length; i++) {
            const comp = result.address_components[i];
            if (
              Array.isArray(comp.types) &&
              comp.types.findIndex((type) => {
                // devLog(`type: ${type}`);
                return type === "country";
              }) >= 0 &&
              countrySearchWhitelist.indexOf(comp.short_name) >= 0
            ) {
              // devLog("Country: ", comp.short_name, comp.long_name);
              bIsValidCountry = true;
              break;
            }
          }
          return bIsValidCountry;
        });
        setAutocompleteList(newLocationsList);
        updateAddressSelected(false);
        setDisplayAutoCompleteList(true);
        props.onDisplayAutoCompleteList?.(true);
      });
    }
  };

  const [addressSelected, setAddressSelected] = useState(false);
  const [expandHighwaySize, setExpandHighwaySize] = useState(true);

  // Notify parent when address selection state changes
  const updateAddressSelected = (value) => {
    setAddressSelected(value);
    if (typeof props.onAddressSelectedChange === "function") {
      props.onAddressSelectedChange(value);
    }
  };

  const handleSelectAddress = async (index) => {
    const address = autocompleteList[index].formatted_address;
    const coords = autocompleteList[index].geometry.location;
    devLog("Geopointing to: ", address);

    if (!originalLocation) {
      setOriginalLocation(coords);
      devLog("setOriginalLocation", coords);
    }
    if (!originalAddress) {
      setOriginalAddress(address);
      devLog("setOriginalAddress", address);
    }

    updateAddressSelected(true);
    setAddressInput(address);
    setTmpCurrentAddress(address);
    setAutocompleteList([]);
    setDisplayAutoCompleteList(false);
    setCurrentLocation(coords);
    setTTLDisablementAddress(address);
    userHasSelectedLocation.current = true; // Mark that user has manually selected a location

    const addressList = autocompleteList[index];
    setAddressList(addressList);
    devLog("Address List:", addressList);
    if (typeof props.onLocationUpdate === "function") {
      // apiRevGeocodeLocationGoogle(coords, (locationData) => {
      //   devLog("LocationData:", locationData);
      //   props.onLocationUpdate(coords, locationData);
      // });
      // devLog("LocationData address:", addressList);
      // devLog("LocationData Coords:", coords);
      props.onLocationUpdate(coords, addressList, true);
      changeZoomTo17();
    }
  };

  const changeZoomTo17 = () => {
    if (typeof props.zoomChange === "function") {
      props.zoomChange(17);
    }
  };

  const returnCurrentLocation = () => {
    if (originalLocation && originalAddress) {
      setLoading(true);
      setCurrentLocation(originalLocation);
      setAddressInput(originalAddress);
      userHasSelectedLocation.current = false; // Reset flag when returning to original location

      apiGetAutocompleteList(originalAddress, originalLocation.lat, originalLocation.lng, (locationsList) => {
        devLog("returnCurrentLocation LocationsList:", locationsList);
        if (typeof props.onLocationUpdate === "function") {
          props.onLocationUpdate(locationsList.results[0].geometry.location, locationsList.results[0]);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  };

  const searchBarStyle = {
    background: "#f4f4f4",
    width: "100%",
    marginLeft: 10,
    marginRight: 10,
    marginBottom: 10,
    marginTop: 10,
    verticalAlign: "middle",
    borderRadius: 10,
    border: "none",
    padding: 10,
  };
  const handleOnHighwayChange = (value) => {
    if (typeof props.handleOnHighwayChange === "function") {
      props.handleOnHighwayChange(highwayInterstateOptions.find((option) => option.value === value));
    }
    setOnHighway(highwayInterstateOptions.find((option) => option.value === value));
    setExpandHighwaySize(value !== undefined ? false : true);
  };

  const highwayInterstateOptions = [
    { value: "true", label: t("description.ConfirmServicesFlatYes") },
    { value: "false", label: t("description.ConfirmServicesFlatNo") },
  ];

  const editBtnStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid gray",
    borderRadius: "20px",
    padding: "3px 10px",
    position: "absolute",
    right: "10px",
    color: "gray",
  };

  return (
    <>
      <div className="autocompleteDisab" style={{ zIndex: 999 }}>
        {" "}
        {/* ref={ref}> */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <p>{t("description.DisablementAutoComplete1")}</p>
          <HelpIcon
            style={{ marginRight: "20px", color: "#0689cd", cursor: "pointer" }}
            onClick={() => {
              devLog("HelpIcon clicked");
              props.setShowOverlay(true);
            }}
          />
        </div>
        <div>
          <div
            className="header flex-row align-items-center"
            // .
            // style={{borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#e4e4e4'}}
            onClick={() => {
              setAutocompleteList([]);
              setDisplayAutoCompleteList(false);
              // setAddressInput("");
            }}
          >
            <input
              type="text"
              value={addressInput}
              onChange={(e) => {
                setAddressInput(e.target.value);
                setTmpCurrentAddress(e.target.value);
                handleAddressInput(e.target.value);
              }}
              disabled={false}
              placeholder={t("description.DisablementAutoComplete2")}
              id={"clickable"}
              style={searchBarStyle}
            />

            <MyLocationIcon
              onClick={() => {
                returnCurrentLocation();
              }}
            />
            <button
              id=""
              type="hidden"
              className="ui-btn"
              onClick={() => {
                setAutocompleteList([]);
                setAddressInput("");
              }}
            >
              <span>
                <ClearIcon />
              </span>
              <ion-icon
                name="chevron-forward-outline"
                role="img"
                class="md hydrated"
                style={{ fontSize: "40px" }}
                aria-label="chevron forward outline"
              />
            </button>
          </div>
          {/* {(!addressComplete || !TTLDisablementCoords) && ( */}
          {/* {(!addressComplete || !TTLDisablementAddress) && (
            <Alert severity="error">
              {t("description.IncompleteAddressMsg")}
            </Alert>
          )} */}
          {/* {!TTLDisablementAddress && (
            <Alert severity="error">
              {t("description.IncompleteAddressMsg")}
            </Alert>
          )} */}
          {/* {addressSelected && ( */}
          <FormControl style={{ paddingLeft: "10px", paddingRight: "10px", width: "100%" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "5px",
                  marginBottom: "5px",
                }}
              >
                <ErrorIcon style={{ color: "#0689cd" }} />
                <div>
                  {expandHighwaySize
                    ? t("description.HighwayInterstateQuestion")
                    : `${t("description.HighwayInterstateAbbreviation")}: ${onHighway.label}`}
                </div>{" "}
                {!expandHighwaySize && (
                  <div style={editBtnStyle}>
                    <EditIcon style={{ color: "gray" }} onClick={() => setExpandHighwaySize(!expandHighwaySize)} />
                  </div>
                )}{" "}
              </div>
              {expandHighwaySize && (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <ErrorIcon className="invisible" />
                  <RadioGroup
                    value={onHighway.value}
                    onChange={(e) => handleOnHighwayChange(e.target.value)}
                    style={{ display: "flex", flexDirection: "row", gap: "10px" }}
                    onClick={(e) => {
                      setExpandHighwaySize(onHighway.value !== undefined ? !expandHighwaySize : true);
                    }}
                  >
                    {highwayInterstateOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>{" "}
                </div>
              )}
            </div>
          </FormControl>
          {/* )} */}
        </div>
        {/* Show user denied location error */}
        {props.userDeniedLocationFlag && <p style={{ color: "red" }}>{t("description.DeniedLocationTest")}</p>}
        {/* Suggested locations */}
        {displayAutoCompleteList && autocompleteList && (
          <div className="autocomplete-list-container">
            {autocompleteList &&
              autocompleteList.length > 0 &&
              autocompleteList.map((location, index) => (
                <div
                  key={index}
                  className="autocomplete-list-item"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    //devLog("autocompleteList index:", index, autocompleteList[index]);
                    handleSelectAddress(index);
                  }}
                >
                  <Divider />
                  <div className="list-item-wrapper">
                    <div className="list-item-label">
                      {location.geometry.location_type === "ROOFTOP" ? <HomeIcon /> : <MyLocationIcon />}
                      <p>{location.formatted_address}</p>
                    </div>
                    <button
                      className="list-item-button selectButton"
                      onClick={(e) => {
                        //devLog("autocompleteList index:", index, autocompleteList[index]);
                        handleSelectAddress(index);
                      }}
                    >
                      Select
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
      {loading && (
        <Modal
          open={true}
          onClose={() => {}}
          closeOnEsc={false}
          center={true}
          closeOnOverlayClick={false}
          showCloseIcon={false}
        >
          <CircularProgress disableShrink />
        </Modal>
      )}
    </>
  );
}

export default AddressAutoComplete;
